import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from '../allauth/auth';
import ApiClient from '../config/ApiClient';
import { ApiListResponse } from '../object-actions/types/types';

interface PointsContextType {
    pointsTotal: number | null;
    loading: boolean;
    error: string | null;
    refreshPoints: () => Promise<void>;
}

const PointsContext = createContext<PointsContextType | null>(null);

export function usePointsContext(): PointsContextType {
    const context = useContext(PointsContext);
    if (!context) {
        throw new Error('usePointsContext must be used within PointsProvider');
    }
    return context;
}

interface Props {
    children: ReactNode;
}

export function PointsProvider({ children }: Props) {
    const auth = useAuth();
    const isAuthenticated = auth?.meta?.is_authenticated === true;

    const [pointsTotal, setPointsTotal] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const refreshPoints = useCallback(async () => {
        if (!isAuthenticated) return;
        setLoading(true);
        setError(null);
        try {
            const response = await ApiClient.get('/api/my-points?limit=1&offset=0');
            if (response.success && response.data) {
                const data = response.data as ApiListResponse<'NguPoints'>;
                const meta = data.meta as { grand_total?: number } | undefined;
                setPointsTotal(meta?.grand_total ?? null);
            } else {
                setError(response.error ?? 'Failed to load points');
            }
        } catch (err) {
            setError('Failed to load points');
            console.error('Error fetching points:', err);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated) {
            refreshPoints();
        } else {
            setPointsTotal(null);
            setError(null);
        }
    }, [isAuthenticated, refreshPoints]);

    const value: PointsContextType = {
        pointsTotal,
        loading,
        error,
        refreshPoints
    };

    return <PointsContext.Provider value={value}>{children}</PointsContext.Provider>;
}
